import React, { type FC } from "react";
import { BusinessList } from "@src/components/business-list";
import { ListItem } from "@src/components/business-list";
import { BusinessLotValue, BusinessMeasureValue, BusinessStockObjValue } from "@src/components/business-data-display";
import { groupSpMovingsByCell, sumSpMovingQty, type SpMoving } from "../../../../../../api/storePlaces";
import type { UseStockPlacementContentsQueryResult } from "../../hooks/useStockPlacementContentsQuery";
import { OverflowMarquee } from "@src/shared/ui";

/** Пропсы компонента {@link StockPlacementContentsList}. */
interface StockPlacementContentsListProps {
    /** Query-result состава приходного ордера. */
    contentsQuery: UseStockPlacementContentsQueryResult;
    /**
     * Открывает форму строки состава.
     * @param contentCode код строки состава
     */
    onOpenContent: (contentCode: number) => void;
}

/** Список состава приходного ордера для размещения ТМЦ. */
export const StockPlacementContentsList: FC<StockPlacementContentsListProps> = ({ contentsQuery, onOpenContent }) => {
    const { content, isFetching } = contentsQuery;

    return (
        <BusinessList isLoad={isFetching}>
            {content.map((item) => {
                const placedQty = sumSpMovingQty(item.SpMovings);
                const isFullyPlaced = placedQty >= item.DepotQty;

                return (
                    <ListItem
                        key={item.Code}
                        tone={isFullyPlaced ? "success" : "default"}
                        title={<BusinessStockObjValue stockObj={item.StockObj} mode="wrap" />}
                        props={[
                            { label: "Партия", value: <BusinessLotValue lot={item.Lot} /> },
                            {
                                label: "Размещено", // x / y, y - Учетное количество (DepotQty).
                                value: (
                                    <BusinessMeasureValue value={`${placedQty} / ${item.DepotQty}`} measure={item.Measure} />
                                ),
                            },
                            {
                                label: "Рекомендуемые МХ",
                                value: <OverflowMarquee>{formatRecommendedCells(item.RecommendedCells)}</OverflowMarquee>,
                            },
                            {
                                label: "Фактическое МХ",
                                value: <OverflowMarquee>{formatPlacedCellsFromSpMovings(item.SpMovings)}</OverflowMarquee>,
                            },
                        ]}
                        onClick={() => {
                            onOpenContent(item.Code);
                        }}
                    />
                );
            })}
        </BusinessList>
    );
};

/**
 * Форматирует обозначение ячейки места хранения.
 * @param cell место хранения с `Num` и `SPIndex`
 */
export function formatCellName(cell: { Num: string; SPIndex: string } | undefined): string {
    if (cell == null) {
        return "";
    }

    return `${cell.Num} - ${cell.SPIndex}`;
}

/**
 * Форматирует список рекомендованных ячеек для отображения в списке/форме.
 * @param cells рекомендованные места хранения
 */
export function formatRecommendedCells(cells: Array<{ Num: string; SPIndex: string }> | undefined): string {
    return cells?.map((cell) => formatCellName(cell)).join(", ") ?? "Не задано";
}

/**
 * Форматирует фактические МХ строки ПО по уникальным `SpCode` из движений размещения.
 * @param movements движения `spMoving` строки состава
 */
export function formatPlacedCellsFromSpMovings(movements: SpMoving[] | undefined): string {
    const cells = groupSpMovingsByCell(movements);
    if (cells.length === 0) {
        return "Не задано";
    }

    const labels = cells.map((cell) => formatCellName(cell.StorePlace)).filter((label) => label !== "");
    return labels.length > 0 ? labels.join(", ") : "Не задано";
}
